/**
 * Questionnaire Process Handlers
 *
 * Questionnaire and questionnaire answer handlers
 */

import dayjs from 'dayjs'
import { handleRequest } from './config'
import * as handlers from '../handlers'
import { Questionnaire, QuestionnaireAnswer } from '../../../../pure-js/types/Questionnaire'
import { QuestionnaireContext } from './typeMappers'

// ========================================
// QUERY HANDLERS
// ========================================

/**
 * Get a questionnaire by user ID and context
 */
export const getQuestionnaire = async (params: { userId: string; context: QuestionnaireContext | string }) => {
  const questionnaireId = `${params.userId}_${params.context}`

  return handleRequest(
    async () => {
      return Promise.resolve({ data: undefined as Questionnaire | undefined, success: true })
    },
    async () => {
      const result = await handlers.directDb.getQuestionnaire({ id: questionnaireId })

      return {
        data: result.data,
        success: result.success,
        error: result.error
      }
    }
  )
}

/**
 * Get a questionnaire answer
 */
export const getQuestionnaireAnswer = async (params: { questionnaireId: string; questionId: string }) => {
  return handleRequest(
    async () => {
      return Promise.resolve({ data: undefined as QuestionnaireAnswer | undefined, success: true })
    },
    async () => {
      const result = await handlers.directDb.getQuestionnaireAnswer(params)

      return {
        data: result.data,
        success: result.success,
        error: result.error
      }
    }
  )
}

// ========================================
// MUTATION HANDLERS
// ========================================

/**
 * Set/create a questionnaire
 */
export const setQuestionnaire = async (params: { userId: string; context: QuestionnaireContext | string }) => {
  const questionnaireId = `${params.userId}_${params.context}`
  const questionnaire: Questionnaire = {
    id: questionnaireId,
    userId: params.userId,
    context: params.context
  } as Questionnaire

  return handleRequest(
    () => {
      return Promise.resolve({ data: questionnaire, success: true })
    },
    async () => {
      const result = await handlers.directDb.updateQuestionnaire(questionnaire)

      return {
        data: result.data,
        success: result.success,
        error: result.error
      }
    }
  )
}

/**
 * Update a questionnaire answer
 */
export const updateQuestionnaireAnswer = async (params: {
  questionnaire: Questionnaire
  questionId: string
  userId: string
  answer: string
  slotId?: string
  textKeyName?: string
}) => {
  const questionnaireAnswer = {
    questionaireId: params.questionnaire.id,
    questionId: params.questionId,
    answer: params.answer,
    slotId: params.slotId || '',
    textKeyName: params.textKeyName || '',
    createdAt: dayjs().format(),
    timestamp: Date.now().toString()
  } as QuestionnaireAnswer

  return handleRequest(
    () => {
      return Promise.resolve({ data: questionnaireAnswer, success: true })
    },
    async () => {
      const result = await handlers.directDb.updateQuestionareAnswer(questionnaireAnswer, params.questionnaire.id)

      return {
        data: result.data,
        success: result.success,
        error: result.error
      }
    }
  )
}

/**
 * Get all questionnaire answers
 */
export const getQuestionnaireAnswers = async (questionnaireId: string) => {
  return handleRequest(
    async () => {
      return Promise.resolve({ data: {} as Record<string, QuestionnaireAnswer>, success: true })
    },
    async () => {
      const result = await handlers.directDb.getQuestionnaireAnswers(questionnaireId)
      return {
        data: result.data,
        success: result.success,
        error: result.error
      }
    }
  )
}

/**
 * Clear a questionnaire and all of its answers.
 */
export const clearQuestionnaire = async (params: { userId: string; context: QuestionnaireContext | string }) => {
  const questionnaireId = `${params.userId}_${params.context}`

  return handleRequest(
    async () => {
      return Promise.resolve({ data: { clearedAnswers: 0 }, success: true })
    },
    async () => {
      try {
        const answersResult = await handlers.directDb.getQuestionnaireAnswers(questionnaireId)
        if (!answersResult.success) {
          throw new Error(answersResult.error ?? 'Failed to load questionnaire answers')
        }

        const answers = answersResult.data || {}
        const answeredQuestions = Object.values(answers).filter((answer) => {
          if (!answer) return false
          const value = typeof answer.answer === 'string' ? answer.answer.trim() : ''
          return value.length > 0
        }).length

        const resetPromises = Object.entries(answers).map(async ([questionId, answerData]) => {
          await handlers.directDb.updateQuestionareAnswer(
            {
              ...(answerData ?? {}),
              answer: '',
              questionId,
              textKeyName: answerData?.textKeyName ?? `gamer.assessmentModal.questions.${questionId}`,
              timestamp: Date.now().toString()
            },
            questionnaireId
          )
        })

        await Promise.all(resetPromises)

        return {
          data: {
            clearedAnswers: answeredQuestions
          },
          success: true
        }
      } catch (error) {
        // Fallback to static reset if dynamic approach fails
        console.error('Failed to load questionnaire answers, using static reset:', error)

        const TOTAL_QUESTIONS = 37
        const questionIds = Array.from({ length: TOTAL_QUESTIONS }, (_, index) => (index + 1).toString())

        await Promise.all(
          questionIds.map(async (questionId) => {
            await handlers.directDb.updateQuestionareAnswer(
              {
                answer: '',
                questionId,
                textKeyName: `gamer.assessmentModal.questions.${questionId}`,
                timestamp: Date.now().toString()
              },
              questionnaireId
            )
          })
        )

        return {
          data: {
            clearedAnswers: TOTAL_QUESTIONS
          },
          success: true
        }
      }
    }
  )
}
